import React, {useEffect, useState} from 'react';
import CalciteButton from '@/components/shared/CalciteButton';
import {scroller} from 'react-scroll';
import PropTypes from 'prop-types';
import {CONTACT_URL, CONTACT_DEV_URL, CONTACT_STG_URL} from '@/constants';

/**
 * Renders a contact button component.
 * @param {Object} props - The component props.
 * @param {string} props.url - The URL to open when the button is clicked.
 * @param {React.ReactNode} props.children - The content of the button.
 * @param {Function} [props.onClick] - Optional callback invoked after the button action completes.
 * @returns {JSX.Element} The rendered contact button component.
 */
const ContactButton = ({url, children, onClick, ...restProps}) => {
  const [anchor, setAnchor] = useState(null);
  const [fallbackUrl, setFallbackUrl] = useState(CONTACT_URL);

  useEffect(() => {
    if (url && /^#/.test(url)) {
      setAnchor(url.substring(1));
    }
  }, [url]);

  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.includes('dev')) {
      setFallbackUrl(CONTACT_DEV_URL);
    } else if (hostname.includes('uat')) {
      setFallbackUrl(CONTACT_STG_URL);
    }
  }, []);

  /**
   * Scrolls to the specified element on the page.
   * @param {string} to - The ID of the element to scroll to.
   * @param {Object} options - Additional options for scrolling.
   */
  const handleScrollTo = (to, options) => {
    scroller.scrollTo(to, {
      smooth: true,
      duration: 500,
      ...options,
    });
  };

  /**
   * Handles the button click event.
   * If an anchor is specified, scrolls to the anchor.
   * Otherwise, opens the URL in a new tab.
   * @param {string} url - The URL to open.
   */
  const handleButtonClick = (url) => {
    if (anchor) {
      handleScrollTo(anchor);
    } else {
      window.open(url, '_blank');
    }
    onClick?.();
  };

  const finalUrl = url || fallbackUrl;

  return (
    <CalciteButton onClick={() => handleButtonClick(finalUrl)} {...restProps}>
      {children}
    </CalciteButton>
  );
};

ContactButton.propTypes = {
  url: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

export default ContactButton;
